import { Link, useLocation } from 'react-router';
import cx from 'classnames';
import { FeatureFlagNames } from '@birdi/js-sdk/src/public/organisation-features';
import { css } from '@emotion/react';
import { PoweredByBirdi } from '@birdi/branding/powered-by-birdi';
import {
  SideBar,
  SidebarLink,
  dividerStyle,
  TitleStyle,
  TitleHolder,
  iconStyle,
  poweredByBirdiStyle,
} from './side-bar-utils';
import { useMetaSelector } from '../../meta/reducer';
import DashboardSVG from '@birdi/icons/2022-home.svg';
import MapSVG from '@birdi/icons/map.svg';
import PilotSVG from '@birdi/icons/user.svg';
import { SidebarSitesSection } from '../../map-sites/SidebarSitesSection';
// import { BetaBadge } from '@birdi/components-common';
import { UserWorkspaces } from '../../user-workspaces/user-workspaces';
import WorkspacesSVG from '@birdi/icons/menu-workspaces.svg';
import ChevronDownSVG from '@birdi/icons/chevron-down.svg';
import { BirdiTheme } from '@birdi/theme';
import { FoldersList, type FoldersListHandle } from './folders/folders-list';
import { MapsFolderMenu } from './folders/maps-folder-menu';
import { useRef, useState } from 'react';
import { hasSites } from '../../map-sites/site-features';

export const workspaceIconStyle = (theme: BirdiTheme) => css`
  background: ${theme.deepOcean};
  padding: 0.25em;
  border-radius: 5px;
  color: ${theme.mono0};
`;

const SideNavigationV2 = () => {
  const location = useLocation();
  const meta = useMetaSelector();
  const foldersListRef = useRef<FoldersListHandle>(null);
  const [folderCount, setFolderCount] = useState(0);
  const jobNotif = meta.jobNotifications;
  const sites = hasSites(meta.features);
  const mapsV2 = meta.orgFeatureFlags.includes(FeatureFlagNames.MapsV2);
  const isWorkspaceContext = !!meta.workspace;
  const menuIcon = isWorkspaceContext
    ? meta.workspace?.menuIcon
    : meta.organisation.menuIcon;

  const workspaceDropdownTrigger = (
    <button
      type="button"
      css={(theme) => css`
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.75em 0.25em;
        width: 100%;
        color: ${theme.midnight};
        border-radius: 7px;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: flex-start;
          align-items: center;
          gap: 0.75em;
        `}
      >
        {menuIcon ? (
          <img
            src={menuIcon}
            alt={'icon'}
            css={css`
              // menu icon size is 40x40px
              width: 1.5em;
              height: 1.5em;
              margin-right: 0.5em;
              border-radius: 3px;
              object-fit: cover; // contain
              border-radius: 5px;
            `}
          />
        ) : (
          <WorkspacesSVG css={workspaceIconStyle} />
        )}
        <span
          css={css`
            font-weight: bold;
            max-width: 12em; // 15-2-1.5-0.75-0.75extra-3
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            display: inline-block;
          `}
        >
          {meta?.workspace?.name || meta.organisation.name}
        </span>
      </div>
      <ChevronDownSVG css={iconStyle} />
    </button>
  );

  return (
    <div
      css={css`
        position: relative;
      `}
    >
      <SideBar>
        <UserWorkspaces trigger={workspaceDropdownTrigger} />
        <hr css={dividerStyle} />
        <Link to={'/'} data-testid="dashboard-nav-link">
          <div
            css={(theme) => css`
              display: flex;
              justify-content: flex-start;
              align-items: center;
              gap: 0.75em;
              padding: 0.75em 0.25em;
              width: cal(100% - 2em);
              background-color: ${'/' === location.pathname
                ? theme.lightOcean
                : theme.mono0};
              color: ${theme.midnight};
              border-radius: 7px;
              &:hover {
                background-color: ${'/' !== location.pathname
                  ? theme.sand
                  : theme.lightOcean};
              }
            `}
          >
            <DashboardSVG css={iconStyle} />
            <span
              css={css`
                font-weight: bold;
              `}
            >
              Dashboard
            </span>
          </div>
        </Link>
        <hr css={dividerStyle} />
        {sites && (
          <>
            <SidebarSitesSection pathname={location.pathname} />
            <hr css={dividerStyle} />
          </>
        )}
        <div
          css={css`
            ${TitleHolder};
            justify-content: space-between;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 0.75em;
            `}
          >
            <MapSVG css={iconStyle} />
            <span css={TitleStyle}>Maps</span>
          </div>
          {mapsV2 && (
            <MapsFolderMenu
              disabled={folderCount === 0}
              onSortAlphabetically={() =>
                foldersListRef.current?.sortAlphabetically()
              }
            />
          )}
        </div>
        {mapsV2 && (
          <>
            <SidebarLink to="/new-maps/all" match={location.pathname} dashboard>
              <span
                css={css`
                  display: inline-flex;
                  align-items: center;
                  gap: 8px;
                `}
              >
                All Maps (Birdi 2.0)
              </span>
            </SidebarLink>
            <FoldersList
              ref={foldersListRef}
              onFolderCountChange={setFolderCount}
            />
            {meta.orgFeatureFlags.includes(FeatureFlagNames.Missions) && (
              <>
                <SidebarLink
                  to="/missions/organisation"
                  match={location.pathname}
                  dashboard
                >
                  <span>Original Maps</span>
                </SidebarLink>
                <SidebarLink
                  to="/missions/shared"
                  match={location.pathname}
                  dashboard
                >
                  <span>Shared With Me</span>
                </SidebarLink>
              </>
            )}
            {/* <SidebarLink
            to="/missions/mine"
            match={location.pathname}
            dashboard
          >
            <span>My Maps</span>
          </SidebarLink> */}
          </>
        )}
        <hr css={dividerStyle} />
        {jobNotif && (
          <>
            <div css={TitleHolder}>
              <PilotSVG css={iconStyle} />
              <span css={TitleStyle}>Pilot Network</span>
            </div>
            <SidebarLink
              to="/job-requests/incoming"
              match={location.pathname}
              dashboard
            >
              <span>Contract Requests</span>
            </SidebarLink>
            <SidebarLink
              to="/missions/mine"
              match={location.pathname}
              dashboard
            >
              <span>Capture Requests</span>
            </SidebarLink>
            <hr css={dividerStyle} />
          </>
        )}
      </SideBar>
      <div css={poweredByBirdiStyle}>
        <PoweredByBirdi mode="dark" />
      </div>
    </div>
  );
};

export default SideNavigationV2;
